import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import PropTypes from 'prop-types';
import PersonalizeUtil from 'personalize_util';
import Spinner from 'core/components/Spinner';
import ApiRequest from 'core/api/request';
import ListingCard from 'core/components/ListingCard';
import Observer from 'core/components/SignupWidget/Observer';
import getQueryParam from 'core/utils/getQueryParam';
import { ScrollEdge, generateTrackingQueryParams } from 'core/utils';
import { ScrollWatch } from 'core/hoc';
import {
	PastBoxHeading,
	Boxes,
	BoxesWrapper,
	Box
} from 'listing/components/PastBoxes/pastBoxesStyles';

import {
	SpinWrapper,
	Title,
	Wrapper,
	ScrollWrapper,
} from './recsStyles';
import CaretBtn from 'core/components/CaretBtn';
import { Divider } from 'listing/layout';

const IN_VIEW_THRESHOLD = 0.50; // 50% of Rec in view = impression

class Recs extends Component {
	timeout = null;

	state = {
		lazyloadHandlerRegistered: false,
		loading: false,
		recs: this.props.recs || [],
		impressions: [],
		strategy: this.props.strategy,
	};

	lazyloadHandler = e => {
		if (! this.props?.recs?.length) {
			if (e.target.dataset.component === "Recs") {
				document.removeEventListener("lazybeforeunveil", this.lazyloadHandler);
				this.getRecs();
			}
		}
	};

	getRecs = async () => {
		if (this.props?.recs?.length) return this.props.recs;
		if (this.state.loading) return;

		this.setState({ loading: true });

		const { strategy } = this.state;
		const { limit, listingId } = this.props;
		let reqParams = { limit: limit, recstrat: strategy };

		if (listingId) {
			reqParams['listing_id'] = listingId;
		}

		// pass-thru `?bust=t` if present
		if (getQueryParam('bust') === 't') {
			reqParams['bust'] = 't';
		}

		try {
			const { results } = await ApiRequest.get('/recs/', reqParams);
			this.setState({
				recs: results,
				loading: false,
			});
		} catch (e) {
			this.setState({ loading: false });
		}
	};

	componentDidMount() {
		if (! this.props?.recs?.length) {
			const searchParams = new URLSearchParams(window.location.search);
			document.addEventListener("lazybeforeunveil", this.lazyloadHandler);
			this.setState({
				lazyloadHandlerRegistered: true,
				strategy: searchParams.get("recstrat") || this.props.strategy,
			});
		}
	}

	componentWillUnmount() {
		if (! this.props?.recs?.length) {
			document.removeEventListener("lazybeforeunveil", this.lazyloadHandler);
		}
	}

	handleInViewport = async (rec) => {
		const impressions = new Set(this.state.impressions);
		impressions.add(rec.listingVersion.listingId);
		this.setState({
			impressions: Array.from(impressions),
		});
		// Fire off impression stat call, unconcerned with responses
		return PersonalizeUtil.handleImpression(
			rec, this.state.strategy, this.props.tracking?.pt
		);
	}

	renderEmptyState = () => {
		if (this.props?.recs?.length) return null;

		if (this.state.loading) {
			return (
				<SpinWrapper>
					<Spinner size="lg" />
				</SpinWrapper>
			);
		}

		if (this.state.lazyloadHandlerRegistered) {
			return (
				<div
					className="lazyload"
					data-component="Recs"
					data-expand="750"
				/>
			);
		}

		return null;
	};

	render() {
		const { strategy, impressions } = this.state;
		const {
			className,
			title,
			renderTitle,
			isLoggedIn,
			isScrollable,
			favoriteIds,
			mobile,
			hideFav,
			tracking,
			compact,
			alignLeft,
			minimum
		} = this.props;

		const cardProps = { compact, mobile, hideFav, isLoggedIn };
		const queryParams = {
			strategy: strategy,
			impressions: impressions.join(","),
		};

		if (this.state.recs.length < minimum) return (
			<div className={className}>
				{renderTitle ? renderTitle() : (<Title $leftAlign={alignLeft}>{title}</Title>)}
				{this.renderEmptyState()}
			</div>
		);

		if (!isScrollable) {
			return (
				<div className={className}>
					{renderTitle ? renderTitle() : (<Title $leftAlign={alignLeft}>{title}</Title>)}
					<Wrapper>
						{this.state.recs.map((rec, idx) => {
							const cjtParam = generateTrackingQueryParams({
								...tracking,
								cn: (idx + 1),
								sn: `recs`,
							});
							return (
								<Observer
									once
									threshold={IN_VIEW_THRESHOLD}
									onObserve={() => this.handleInViewport(rec)}
									key={`recId-${rec.listingVersion.listingId}`}>
									{({ observerRef }) => (
										<div ref={observerRef}>
											<ListingCard
												{...cardProps}
												listing={rec.listingVersion}
												favorited={favoriteIds.includes(rec.listingVersion.listingId)}
												lazy={true}
												queryParams={{
													...queryParams,
													cjt: cjtParam,
													strategy: rec.strategy || strategy,
												}}
											/>
										</div>
									)}
								</Observer>
							);
						})}
					</Wrapper>
				</div>
			);
		}

		return (
			/* For a smaller, side-scrolling list */
			<ScrollWrapper id="box-recs-wrapper" className={className}>
				{renderTitle ? renderTitle() : (<PastBoxHeading>{title}</PastBoxHeading>)}
				<ScrollWatch>
					{({ wrapperRef, scrollPercentX, scrollTo, noScrollXYet }) => (
						<BoxesWrapper $compact={compact}>
							<CaretBtn center flip left="4px" onClick={scrollTo(-400)} hide={noScrollXYet} alt="Slide Forward" />
							<Boxes ref={wrapperRef} $compact={compact}>
								<>
									{this.state.recs.map((rec, idx) => (
										<Observer
											once
											threshold={IN_VIEW_THRESHOLD}
											onObserve={() => this.handleInViewport(rec)}
											key={`recId-${rec.listingVersion.listingId}`}>
											{({ observerRef }) => {
												const cjtParam = generateTrackingQueryParams({
													...tracking,
													cn: (idx + 1),
													sn: `recs`,
												});
												return (
													<Box
														as="div"
														ref={observerRef}
														className="listing-card-column">
														<ListingCard
															className="listing-card"
															{...cardProps}
															hideDescription={compact || !!mobile}
															listing={rec.listingVersion}
															favorited={favoriteIds.includes(rec.listingVersion.listingId)}
															lazy={true}
															queryParams={{
																...queryParams,
																cjt: cjtParam,
																strategy: rec.strategy || strategy,
															}}
														/>
													</Box>
												);
											}}
										</Observer>
									))}
								</>
							</Boxes>
							<CaretBtn center right="4px" onClick={scrollTo(400)} hide={scrollPercentX > ScrollEdge} alt="Slide Forward" />
						</BoxesWrapper>
					)}
				</ScrollWatch>
				{!compact && (<Divider />)}
			</ScrollWrapper>
		);
	}

	static propTypes = {
		listingId: PropTypes.number,
		minimum: PropTypes.number,
		title: PropTypes.string,
		strategy: PropTypes.string,
		limit: PropTypes.oneOfType([
			PropTypes.number,
			PropTypes.string,
		]),
		renderTitle: PropTypes.func,
		className: PropTypes.string,
		isLoggedIn: PropTypes.bool,
		isScrollable: PropTypes.bool, // Whether list should side scroll
		mobile: PropTypes.bool,
		hideFav: PropTypes.bool,
		favoriteIds: PropTypes.arrayOf(PropTypes.number),
		tracking: PropTypes.object,
		recs: PropTypes.array,
		compact: PropTypes.bool,
		alignLeft: PropTypes.bool,
	};

	static defaultProps = {
		// NOTE: We mostly set `aws_related_sims` as the strategy.
		// Using `featured_category` as default bc it doesn't rely on 3rd party tech
		strategy: 'featured_category',
		minimum: 2,
		title: 'Customers who bought this also bought...',
		limit: 4,
		renderTitle: null,
		favoriteIds: [],
		tracking: {},
	};
}

export default hot(module)(Recs);
